import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as ExcelJS from 'exceljs';
import { useAuth } from '../../components/auth/AuthContext';
import ClientTradesGrid from '../../components/grids/ClientTradesGrid';
import { clientService as clientApiService, TradingEntity } from '../../services/api/clientService';
import { WorkflowState } from '../../types/workflow';
import { logger } from '../../utils/logger';
import { useSystemDate } from '../../hooks/useSystemDate';
import './Positions.css';
import '../../styles/FilterButtons.css';

const WORKFLOW_FILTERS: Array<{ key: string; states?: WorkflowState[] }> = [
  { key: 'all' },
  { key: 'Booked', states: ['Booked'] },
  { key: 'Confirmed', states: ['Confirmed'] },
  { key: 'Cancelled', states: ['Cancelled'] },
];

const ConfirmationsReport: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;

  const [entities, setEntities] = useState<TradingEntity[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('all');
  const [refreshTrigger] = useState(0);
  const [selectedTrades, setSelectedTrades] = useState<any[]>([]);
  const [selectAllTrigger, setSelectAllTrigger] = useState(0);
  const [clearAllTrigger, setClearAllTrigger] = useState(0);
  const [tradeNumberSearch, setTradeNumberSearch] = useState('');
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);
  const [confReceivedFilter, setConfReceivedFilter] = useState<string>('all');
  const [confStatusFilter, setConfStatusFilter] = useState<string>('all');
  const [workflowFilter, setWorkflowFilter] = useState<string>('all');

  const systemDateResult = useSystemDate(clientId);
  const maturityDateFrom = filterActiveOnly ? systemDateResult.systemDate : undefined;

  const activeWorkflowStates = useMemo(() => {
    const found = WORKFLOW_FILTERS.find(f => f.key === workflowFilter);
    return found?.states;
  }, [workflowFilter]);

  const confirmationReceivedFilter = confReceivedFilter === 'all' ? undefined : confReceivedFilter;
  const confirmationStatusFilter = confStatusFilter === 'all' ? undefined : confStatusFilter;

  // Load trading entities for the filter
  useEffect(() => {
    const loadEntities = async () => {
      if (!clientId) return;
      try {
        const response = await clientApiService.getTradingEntities(clientId);
        if (response.success && response.data) {
          setEntities(response.data);
        }
      } catch (error) {
        logger.error('Failed to load trading entities:', error);
      }
    };
    loadEntities();
  }, [clientId]);

  const handleEntityChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedEntityId(e.target.value);
    setSelectedTrades([]);
  }, []);

  const handleMultiSelectionChanged = useCallback((trades: any[]) => {
    setSelectedTrades(trades);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilterActiveOnly(false);
    setConfReceivedFilter('all');
    setConfStatusFilter('all');
    setWorkflowFilter('all');
    setTradeNumberSearch('');
    setSelectedEntityId('all');
  }, []);

  // Helper to get display value for confirmation received
  const getConfReceivedDisplay = (trade: any): string => {
    return trade.status === 'matched' ? t('grid.values.yes') : t('grid.values.no');
  };

  // Helper to get display value for confirmation status
  const getConfStatusDisplay = (trade: any): string => {
    const cs = trade.confirmationStatus;
    if (!cs) return '';
    if (cs === 'ok') return t('grid.values.ok');
    if (cs === 'provisional') return t('grid.values.provisional', 'Provisional');
    return t('grid.values.difference');
  };

  // Helper to get display value for settlement instruction
  const getSettlementInstructionDisplay = (trade: any): string => {
    return trade.settlementInstructionStatus === 'generated' ? t('grid.values.yes') : t('grid.values.no');
  };

  // Helper to get display value for approval
  const getApprovalDisplay = (trade: any): string => {
    const pending = trade.pendingApproval;
    if (pending) {
      const current = pending.currentApprovals?.length || 0;
      const required = pending.requiredApprovers || 1;
      return `${current}/${required}`;
    }
    return '';
  };

  // Export headers matching the on-screen AG Grid column order
  const exportHeaders = [
    'Confirmation Received', 'Confirmation Status', 'Settlement Instruction',
    'Workflow State', 'Approval',
    'Trade #', 'Our Party', 'Bank Trade #', 'Counterparty', 'Product',
    'Trade Date', 'Value Date', 'Direction', 'CCY1', 'Amount CCY1',
    'Rate', 'Spot Price', 'Forward Points', 'CCY2', 'Amount CCY2',
    'Maturity Date', 'Fixing Reference',
    'Settlement Type', 'Settlement CCY', 'Payment Date',
    'CCY1 Payment Date', 'CCY2 Payment Date', 'Fixing Offset',
    'Grouping 1', 'Grouping 2', 'Grouping 3', 'Hedge Object',
  ];

  // Build a row array matching exportHeaders order
  const buildExportRow = (trade: any) => [
    getConfReceivedDisplay(trade),
    getConfStatusDisplay(trade),
    getSettlementInstructionDisplay(trade),
    trade.workflowState || '',
    getApprovalDisplay(trade),
    trade.TradeNumber, trade.OurParty, trade.bankTradeNumber, trade.CounterpartyName, trade.ProductType,
    trade.TradeDate, trade.ValueDate, trade.Direction, trade.Currency1, trade.QuantityCurrency1,
    trade.Price, trade.SpotPrice, trade.ForwardPoints, trade.Currency2, trade.QuantityCurrency2,
    trade.MaturityDate, trade.FixingReference,
    trade.SettlementType, trade.SettlementCurrency, trade.PaymentDate,
    trade.Currency1PaymentDate, trade.Currency2PaymentDate, trade.FixingOffset,
    trade.Grouping1, trade.Grouping2, trade.Grouping3, trade.HedgeObject,
  ];

  const handleExportCsv = useCallback(() => {
    if (selectedTrades.length === 0) return;

    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const rows = selectedTrades.map(trade => buildExportRow(trade).map(escape).join(','));

    const csv = [exportHeaders.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `confirmations_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedTrades, t]);

  const handleExportExcel = useCallback(async () => {
    if (selectedTrades.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Confirmations Report');

    // Header row
    const headerRow = worksheet.addRow(exportHeaders);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF2D2D2D' },
      };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });

    // Data rows — convert numeric fields to numbers for Excel formatting
    for (const trade of selectedTrades) {
      const row = buildExportRow(trade);
      // Convert amount/rate fields to numbers (indices based on exportHeaders order)
      // 14: Amount CCY1, 15: Rate, 16: Spot Price, 17: Forward Points, 19: Amount CCY2
      [14, 15, 16, 17, 19].forEach(i => {
        if (row[i] != null) row[i] = Number(row[i]) || row[i];
      });
      worksheet.addRow(row);
    }

    // Number formats for amount/rate columns (1-indexed in ExcelJS)
    const numFmt2 = '#,##0.00';
    const numFmt4 = '#,##0.0000';
    worksheet.getColumn(15).numFmt = numFmt2;  // Amount CCY1
    worksheet.getColumn(16).numFmt = numFmt4;  // Rate
    worksheet.getColumn(17).numFmt = numFmt4;  // Spot Price
    worksheet.getColumn(18).numFmt = numFmt4;  // Forward Points
    worksheet.getColumn(20).numFmt = numFmt2;  // Amount CCY2

    // Auto-fit column widths (approximate based on header length)
    worksheet.columns.forEach((col) => {
      const header = col.header as string || '';
      col.width = Math.max(header.length + 2, 12);
    });

    // Freeze header row
    worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 0 }];

    // Download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `confirmations_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedTrades, t]);

  return (
    <div className="positions-container">
      <div className="positions-header">
        <h1>{t('confirmationsReport.title')}</h1>
      </div>

      <div className="positions-filters">
        <div className="filter-group">
          <label>{t('positions.entity')}</label>
          <select
            className="positions-select"
            value={selectedEntityId}
            onChange={handleEntityChange}
          >
            <option value="all">{t('positions.allEntities')}</option>
            {entities.map(entity => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>{t('positions.tradeSearch', 'Trade #')}</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              className="positions-select"
              value={tradeNumberSearch}
              onChange={(e) => setTradeNumberSearch(e.target.value)}
              placeholder={t('positions.tradeSearchPlaceholder', 'Filter by trade number...')}
              style={{ width: '180px' }}
            />
            <button
              className={`positions-select-all-btn${filterActiveOnly ? ' positions-filter-active' : ''}`}
              onClick={() => setFilterActiveOnly(prev => !prev)}
              title={t('confirmationsReport.filterActiveTooltip')}
            >
              {t('confirmationsReport.filterActive')}
            </button>
          </div>
        </div>
        <div className="filter-group">
          <label>{t('confirmationsReport.confReceivedLabel')}</label>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {['all', 'matched', 'unmatched'].map(key => (
              <button
                key={key}
                className={`positions-select-all-btn${confReceivedFilter === key ? ' positions-filter-active' : ''}`}
                onClick={() => setConfReceivedFilter(key)}
              >
                {key === 'all' ? t('confirmationsReport.allStates') : key === 'matched' ? t('grid.values.yes') : t('grid.values.no')}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group">
          <label>{t('confirmationsReport.confStatusLabel')}</label>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {['all', 'ok', 'difference'].map(key => (
              <button
                key={key}
                className={`positions-select-all-btn${confStatusFilter === key ? ' positions-filter-active' : ''}`}
                onClick={() => setConfStatusFilter(key)}
              >
                {key === 'all' ? t('confirmationsReport.allStates') : key === 'ok' ? t('grid.values.ok') : t('grid.values.difference')}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group">
          <label>{t('confirmationsReport.workflowFilter')}</label>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {WORKFLOW_FILTERS.map(wf => (
              <button
                key={wf.key}
                className={`positions-select-all-btn${workflowFilter === wf.key ? ' positions-filter-active' : ''}`}
                onClick={() => setWorkflowFilter(wf.key)}
              >
                {wf.key === 'all' ? t('confirmationsReport.allStates') : wf.key}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={handleClearFilters}
          className="action-btn clear-btn"
        >
          {t('confirmationsReport.clearFilters')}
        </button>
        <button
          className="positions-select-all-btn"
          onClick={() => setSelectAllTrigger(t => t + 1)}
        >
          {t('positions.selectAll')}
        </button>
        <button
          className="positions-select-all-btn"
          onClick={() => setClearAllTrigger(t => t + 1)}
        >
          {t('positions.clearAll')}
        </button>
        <button
          className="positions-select-all-btn positions-export-btn"
          onClick={handleExportCsv}
          disabled={selectedTrades.length === 0}
          title={selectedTrades.length === 0 ? t('confirmationsReport.exportCsvHint') : undefined}
        >
          {t('confirmationsReport.exportCsv')}
        </button>
        <button
          className="positions-select-all-btn positions-export-btn"
          onClick={handleExportExcel}
          disabled={selectedTrades.length === 0}
          title={selectedTrades.length === 0 ? t('confirmationsReport.exportExcelHint') : undefined}
        >
          {t('confirmationsReport.exportExcel')}
        </button>
      </div>

      <div className="positions-content chart-collapsed">
        <div className="positions-grid-wrapper">
          <ClientTradesGrid
            refreshTrigger={refreshTrigger}
            hideUploadControls={true}
            hideConfirmationColumns={false}
            menuContext="none"
            multiSelect={true}
            onMultiSelectionChanged={handleMultiSelectionChanged}
            entityFilter={selectedEntityId === 'all' ? undefined : selectedEntityId}
            suppressDefaultSort={true}
            selectAllTrigger={selectAllTrigger}
            clearAllTrigger={clearAllTrigger}
            tradeNumberSearch={tradeNumberSearch || undefined}
            maturityDateFrom={maturityDateFrom}
            workflowStates={activeWorkflowStates}
            confirmationReceivedFilter={confirmationReceivedFilter}
            confirmationStatusFilter={confirmationStatusFilter}
          />
        </div>
      </div>
    </div>
  );
};

export default ConfirmationsReport;
